import React, { useState, useEffect } from 'react';
import CountdownTimer from '../Voting/CountdownTimer';

interface ImageItem {
  id?: string;
  _id?: string;
  url: string;
  thumbnailUrl?: string;
  description?: string;
  alt?: string;
}

interface VotingSession {
  sessionId?: string;
  sessionNumber?: number;
  status: 'scheduled' | 'active' | 'completed' | 'canceled';
  startDate: string | Date;
  endDate: string | Date;
  question?: string;
  type?: 'open' | 'anonymous';
}

interface ProjectCardCarouselProps {
  images: ImageItem[];
  scheduledVotingSession?: VotingSession | null;
  activeVotingSession?: VotingSession | null;
  projectTitle: string;
  className?: string;
  onVoteClick?: (e?: React.MouseEvent) => void;
}

const ProjectCardCarousel: React.FC<ProjectCardCarouselProps> = ({
  images,
  scheduledVotingSession,
  activeVotingSession,
  projectTitle,
  className = '',
  onVoteClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Build carousel items: voting sessions first (priority), then images, then generic image
  const carouselItems: Array<{
    type: 'vote_active' | 'vote_scheduled' | 'image' | 'generic';
    data: any;
  }> = [];

  // Active voting session has highest priority
  if (activeVotingSession && activeVotingSession.status === 'active') {
    carouselItems.push({
      type: 'vote_active',
      data: activeVotingSession,
    });
  }

  // Scheduled voting session comes next
  if (scheduledVotingSession && scheduledVotingSession.status === 'scheduled') {
    carouselItems.push({
      type: 'vote_scheduled',
      data: scheduledVotingSession,
    });
  }

  // Add images
  images.forEach((img) => {
    carouselItems.push({
      type: 'image',
      data: img,
    });
  });

  // If no items at all, add generic image
  if (carouselItems.length === 0) {
    carouselItems.push({
      type: 'generic',
      data: null,
    });
  }

  // Auto-play for images only (not voting sessions)
  useEffect(() => {
    if (carouselItems.length > 1 && carouselItems[currentIndex].type === 'image') {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % carouselItems.length;
          // Skip voting sessions when auto-playing
          if (carouselItems[next].type === 'vote_active' || carouselItems[next].type === 'vote_scheduled') {
            return (next + 1) % carouselItems.length;
          }
          return next;
        });
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [carouselItems.length, currentIndex]);

  if (carouselItems.length === 0) {
    return null;
  }

  const currentItem = carouselItems[currentIndex];
  const hasMultipleItems = carouselItems.length > 1;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const renderItem = () => {
    switch (currentItem.type) {
      case 'vote_active':
        return (
          <div className="relative aspect-video bg-gradient-to-r from-[var(--color-success-600)] to-[var(--color-success-500)] flex items-center justify-center">
            <div className="text-center text-white p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-bold">Voting Active</h3>
              </div>
              {currentItem.data.question && (
                <p className="text-white/80 mb-4 text-sm line-clamp-2">{currentItem.data.question}</p>
              )}
              {onVoteClick && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onVoteClick();
                  }}
                  className="mt-2 bg-white text-[var(--color-success-600)] font-semibold px-4 py-2 rounded-lg hover:bg-[var(--color-success-50)] transition-colors shadow-lg animate-pulse text-sm"
                >
                  Vote Now
                </button>
              )}
            </div>
          </div>
        );

      case 'vote_scheduled':
        return (
          <div className="relative aspect-video bg-gradient-to-r from-[var(--color-brand-600)] to-[var(--color-brand-500)] flex items-center justify-center">
            <div className="text-center text-white p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-bold">Voting Scheduled</h3>
              </div>
              {currentItem.data.question && (
                <p className="text-white/80 mb-4 text-sm line-clamp-2">{currentItem.data.question}</p>
              )}
              <div className="mt-4">
                <CountdownTimer targetDate={currentItem.data.startDate} variant="hero" />
              </div>
            </div>
          </div>
        );

      case 'image':
        const imageUrl = currentItem.data.thumbnailUrl || currentItem.data.url;
        const imageAlt = currentItem.data.alt || currentItem.data.description || projectTitle;
        return (
          <div className="relative aspect-video bg-[var(--color-neutral-200)]">
            <img
              src={imageUrl}
              alt={imageAlt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        );

      case 'generic':
      default:
        // Generic placeholder image
        return (
          <div className="relative aspect-video bg-gradient-to-br from-[var(--color-neutral-200)] to-[var(--color-neutral-300)] flex items-center justify-center">
            <div className="text-center text-[var(--color-subtext-color)]">
              <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm font-medium">{projectTitle}</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-lg ${className}`}>
      {renderItem()}
      {hasMultipleItems && (
        <>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full transition-colors z-10"
            aria-label="Previous"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full transition-colors z-10"
            aria-label="Next"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {carouselItems.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToSlide(index);
                }}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectCardCarousel;

